/**
 * NavSearch Component
 * Search-based navigation filtering for sidebar menu
 */

import React, { useState, useEffect } from 'react';
import { Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

interface NavSearchProps {
  onFilter: (query: string) => void;
  collapsed?: boolean;
  onExpandSidebar?: () => void;
  tone?: 'dark' | 'light';
}

export const NavSearch: React.FC<NavSearchProps> = React.memo(
  ({ onFilter, collapsed, onExpandSidebar, tone = 'dark' }) => {
    const { t } = useTranslation('common');
    const [query, setQuery] = useState('');
    const prefixColor = tone === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';

    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
          e.preventDefault();
          if (collapsed && onExpandSidebar) {
            onExpandSidebar();
          }
          // Focus with slight delay to allow sidebar expand animation
          setTimeout(() => {
            const input = document.getElementById('nav-search-input');
            input?.focus();
          }, 100);
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [collapsed, onExpandSidebar]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setQuery(value);
      onFilter(value);
    };

    if (collapsed) {
      return (
        <div
          style={{ padding: '8px 16px', textAlign: 'center', cursor: 'pointer' }}
          onClick={onExpandSidebar}
        >
          <SearchOutlined style={{ color: 'rgba(255,255,255,0.65)', fontSize: 16 }} />
        </div>
      );
    }

    return (
      <div style={{ padding: '8px 12px' }}>
        <Input
          className={`nav-search-input nav-search-input-${tone}`}
          id="nav-search-input"
          placeholder={t('search.menu')}
          prefix={<SearchOutlined style={{ color: prefixColor }} />}
          value={query}
          onChange={handleChange}
          allowClear
          size="small"
        />
      </div>
    );
  }
);

NavSearch.displayName = 'NavSearch';
